import cn from 'classnames'
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'

import { getRandomValue } from '@shared'

import styles from './DustedText.module.scss'

type Props = {
  text: string
  type?: 'words' | 'chars'
  className?: string
  particleClassName?: string
  hidden?: boolean
  hasInitAnimation?: boolean
  withSpaces?: boolean
}

type Phase = 'animating' | 'collapsed'

export const DustedText: FC<Props> = memo(function DustedText({
  text,
  className,
  particleClassName = '',
  hidden = false,
  hasInitAnimation = true,
  withSpaces = true,
  type = 'words',
}: Props) {
  const words = useMemo(() => text.split(type === 'words' ? ' ' : ''), [text, type])
  const [phase, setPhase] = useState<Phase>('animating')

  const wordsCssProps = useMemo(
    () =>
      words.map(() => ({
        '--animation-delay': `0.4s`,
        '--initial-scale': `${getRandomValue(2, 3)}`,
        '--initial-transform-x': `${getRandomValue(-10, 10)}px`,
        '--initial-transform-y': `${getRandomValue(-10, 10)}px`,
        '--initial-blur': `${getRandomValue(0, 10)}px`,
      })),
    [words],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (phase === 'collapsed') return

    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          setPhase('collapsed')
          observer.disconnect()
        }
      },
      { threshold: 0 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [phase])


  return (
    <div ref={containerRef} className={cn(styles.dustedText, className)}>
      {phase === 'collapsed' ? (
        <span className={cn({[particleClassName]: particleClassName})}>{text}</span>
      ) : (
        words.map((word, i) => (
          <span
            key={`${word}-${i}`}
            className={cn(styles.word, {
              [styles.wordInit]: !hidden && hasInitAnimation,
              [styles.wordExit]: hidden,
              [styles.wordWithSpaces]: withSpaces,
              [particleClassName]: particleClassName,
            })}
            style={wordsCssProps[i] as React.CSSProperties}
          >
            {word}
          </span>
        ))
      )}
    </div>
  )
})
